import _ from 'lodash';
import { MediaAsset, OpenFolderResponse } from '../../types/media-assets';
import { StringMap } from '../../types/system';
import { FILES_ACTIONS } from '../files/files.actions';
import { JOBS_ACTION_NAMES } from '../jobs/jobs.actions';
import { Action } from '../redux.types';
import { FILE_MANAGER_COMPONENT_ACTIONS } from './file-manager-component.actions';
import {
  defaultFileManagerState,
  FILE_MANAGER_PAGE_COMPONENT_ID,
  FileManagerComponentState,
  SingleFileManagerComponentState,
} from './file-manager-component.types';
import {
  appendCreatedMediaAsset,
  appendFileCreatedByJob,
  appendLoadedFiles,
  appendUploadedFile,
  deleteFile,
  setCurrentPath,
  setLoadedFiles,
  setLoadingError,
} from './sub-reducers/current-folder.reducer';
import {
  savePageForLoadedFiles,
  setLoadingFailed,
  setLoadingStarted,
} from './sub-reducers/pagination.reducer';
import { setCreateNewFolderMode } from './sub-reducers/ui-state.reducer';

type BehaviorFn = (
  state: SingleFileManagerComponentState,
  payload: any,
) => SingleFileManagerComponentState;

type Behavior = BehaviorFn | { applyForAll: boolean; handler: BehaviorFn };

type ActionHandlersConfig = StringMap<Behavior>;

const BEHAVIORS: ActionHandlersConfig = {
  [FILE_MANAGER_COMPONENT_ACTIONS.INIT_COMPONENT]: (
    state: SingleFileManagerComponentState,
  ): SingleFileManagerComponentState => {
    return state;
  },
  [FILE_MANAGER_COMPONENT_ACTIONS.SET_CURRENT_PATH_IN_MEMORY]: (
    state: SingleFileManagerComponentState,
    payload: string,
  ): SingleFileManagerComponentState => {
    return {
      ...state,
      currentFolder: setCurrentPath(state.currentFolder, payload),
    };
  },
  [FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_GET_SUCCESS]: (
    state: SingleFileManagerComponentState,
    payload: OpenFolderResponse,
  ): SingleFileManagerComponentState => {
    return {
      ...state,
      pagination: savePageForLoadedFiles(state.pagination, payload),
      currentFolder: setLoadedFiles(state.currentFolder, payload),
    };
  },
  [FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_GET_ERROR]: (
    state: SingleFileManagerComponentState,
    payload,
  ): SingleFileManagerComponentState => {
    return {
      ...state,
      currentFolder: setLoadingError(state.currentFolder, payload),
    };
  },
  [FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_LOAD_MORE_SUCCESS]: (
    state: SingleFileManagerComponentState,
    payload,
  ): SingleFileManagerComponentState => {
    return {
      ...state,
      pagination: savePageForLoadedFiles(state.pagination, payload),
      currentFolder: appendLoadedFiles(state.currentFolder, payload),
    };
  },
  [FILES_ACTIONS.FOLDER_CREATE_SUCCESS]: {
    applyForAll: true,
    handler: (
      state: SingleFileManagerComponentState,
      payload: MediaAsset,
    ): SingleFileManagerComponentState => {
      return {
        ...state,
        currentFolder: appendCreatedMediaAsset(state.currentFolder, payload),
      };
    },
  },
  [FILES_ACTIONS.FILE_DELETE_SUCCESS]: {
    applyForAll: true,
    handler: (
      state: SingleFileManagerComponentState,
      payload,
    ): SingleFileManagerComponentState => {
      return {
        ...state,
        currentFolder: deleteFile(state.currentFolder, payload),
      };
    },
  },
  [FILES_ACTIONS.FILE_UPLOAD_SUCCESS]: {
    applyForAll: true,
    handler: (
      state: SingleFileManagerComponentState,
      payload,
    ): SingleFileManagerComponentState => {
      return {
        ...state,
        currentFolder: appendUploadedFile(state.currentFolder, payload),
      };
    },
  },
  [FILES_ACTIONS.FILE_CREATE]: {
    applyForAll: true,
    handler: (
      state: SingleFileManagerComponentState,
      payload,
    ): SingleFileManagerComponentState => {
      return {
        ...state,
        currentFolder: appendUploadedFile(state.currentFolder, payload),
      };
    },
  },
  [JOBS_ACTION_NAMES.JOB_STATUS_CHANGED]: {
    applyForAll: true,
    handler: (
      state: SingleFileManagerComponentState,
      payload,
    ): SingleFileManagerComponentState => {
      return {
        ...state,
        currentFolder: appendFileCreatedByJob(state.currentFolder, payload),
      };
    },
  },
  [FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_LOAD_MORE_START]: (
    state: SingleFileManagerComponentState,
  ): SingleFileManagerComponentState => {
    return {
      ...state,
      pagination: setLoadingStarted(state.pagination),
    };
  },
  [FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_LOAD_MORE_ERROR]: (
    state: SingleFileManagerComponentState,
  ): SingleFileManagerComponentState => {
    return {
      ...state,
      pagination: setLoadingFailed(state.pagination),
    };
  },

  [FILE_MANAGER_COMPONENT_ACTIONS.SHOW_CREATE_NEW_FOLDER_INPUT]: (
    state: SingleFileManagerComponentState,
  ): SingleFileManagerComponentState => {
    return {
      ...state,
      uiState: setCreateNewFolderMode(true),
    };
  },
  [FILE_MANAGER_COMPONENT_ACTIONS.HIDE_CREATE_NEW_FOLDER_INPUT]: (
    state: SingleFileManagerComponentState,
  ): SingleFileManagerComponentState => {
    return {
      ...state,
      uiState: setCreateNewFolderMode(false),
    };
  },
};

const updateStateByComponentId =
  (
    behaviors: ActionHandlersConfig,
    defaultState: SingleFileManagerComponentState,
  ) =>
  (
    state: FileManagerComponentState = {},
    action: Action,
  ): FileManagerComponentState => {
    const { meta, type, payload } = action;
    const behavior = behaviors[type];

    if (!behavior) {
      return state;
    }

    const componentId =
      meta && meta.componentId
        ? meta.componentId
        : FILE_MANAGER_PAGE_COMPONENT_ID;

    if (_.isFunction(behavior) || !behavior.applyForAll) {
      const handler: BehaviorFn = behavior as BehaviorFn;
      return {
        ...state,
        [componentId]: handler(
          _.has(state, componentId) ? state[componentId] : defaultState,
          payload,
        ),
      };
    }

    const handler: BehaviorFn = behavior.handler;

    return _.reduce(
      state,
      (
        prevResult: FileManagerComponentState,
        component: SingleFileManagerComponentState,
        key: string,
      ) => {
        return {
          ...prevResult,
          [key]: handler(component, payload),
        };
      },
      {},
    );
  };

export const fileManagerComponent = updateStateByComponentId(
  BEHAVIORS,
  defaultFileManagerState,
);
